import { useCallback, useEffect, useState } from 'react';

/** Documento con los prefijos que todavía usan Safari y navegadores antiguos. */
interface DocumentoConPrefijos extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

/** Elemento raíz con el prefijo de Safari. */
interface ElementoConPrefijos extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

function elementoEnPantallaCompleta(): Element | null {
  if (typeof document === 'undefined') return null;

  const doc = document as DocumentoConPrefijos;

  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function haySoporte(): boolean {
  if (typeof document === 'undefined') return false;

  return typeof document.documentElement.requestFullscreen === 'function';
}

interface UsePantallaCompleta {
  /** El navegador permite usar la API (si no, el botón no se muestra). */
  disponible: boolean;
  /** La página está ocupando toda la pantalla. */
  esCompleto: boolean;
  /** Entra o sale del modo pantalla completa. */
  alternar: () => Promise<void>;
}

/**
 * Pantalla completa del navegador (la misma que F11, pero sin ocultar barras a mano).
 *
 * - Usa `document.documentElement`, así que cubre toda la aplicación (login incluido).
 * - Se mantiene sincronizado con `fullscreenchange`: si el usuario sale con `Esc`, el
 *   icono del botón vuelve a su estado inicial.
 * - La llamada puede ser rechazada (por ejemplo, si no viene de un clic del usuario);
 *   en ese caso se ignora en silencio y el estado real lo confirma el evento.
 */
export function usePantallaCompleta(): UsePantallaCompleta {
  const [esCompleto, setEsCompleto] = useState(() => elementoEnPantallaCompleta() !== null);
  const [disponible] = useState(haySoporte);

  useEffect(() => {
    const alCambiar = () => setEsCompleto(elementoEnPantallaCompleta() !== null);

    document.addEventListener('fullscreenchange', alCambiar);
    document.addEventListener('webkitfullscreenchange', alCambiar);

    // El estado puede haber cambiado entre el primer render y el montaje.
    alCambiar();

    return () => {
      document.removeEventListener('fullscreenchange', alCambiar);
      document.removeEventListener('webkitfullscreenchange', alCambiar);
    };
  }, []);

  const alternar = useCallback(async () => {
    if (typeof document === 'undefined') return;

    const doc = document as DocumentoConPrefijos;
    const raiz = document.documentElement as ElementoConPrefijos;

    try {
      if (elementoEnPantallaCompleta()) {
        await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      } else {
        await (raiz.requestFullscreen?.({ navigationUI: 'hide' }) ??
          raiz.webkitRequestFullscreen?.());
      }
    } catch {
      // El navegador puede rechazar la petición (gesto no reconocido, política del
      // marco…). No hay nada que mostrar: el evento de cambio deja el estado correcto.
    }
  }, []);

  return { disponible, esCompleto, alternar };
}
